import { CancelFullIcon } from '@hedepy/icons/refactor/CancelFull'
import { cva, type VariantProps } from 'class-variance-authority'
import { Dialog as DialogPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/legacy-src/lib/utils'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import { Heading } from '@/src/domains/design-system/components/Heading'
import { Text } from '@/src/domains/design-system/components/Text'

export interface DialogProps extends DialogPrimitive.DialogProps {}

export const Dialog = DialogPrimitive.Root

export interface DialogTriggerProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger> {}

export const DialogTrigger = DialogPrimitive.Trigger

export interface DialogPortalProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Portal> {}

export const DialogPortal = DialogPrimitive.Portal

export interface DialogCloseProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close> {}

export const DialogClose = DialogPrimitive.Close

export interface DialogOverlayProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> {}

export const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, DialogOverlayProps>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-dialogOverlay bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  ),
)
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

export enum DialogMobilePosition {
  BOTTOM = 'bottom',
  CENTER = 'center',
}

const dialogContentVariants = cva(
  'fixed left-[50%] z-dialog flex translate-x-[-50%] flex-col gap-4 bg-white-500 p-5 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] tablet:bottom-auto tablet:top-[50%] tablet:max-h-[calc(100%-2.5rem)] tablet:w-[calc(100%-2.5rem)] tablet:max-w-[var(--maxWidth)] tablet:translate-y-[-50%] tablet:rounded-xl',
  {
    variants: {
      mobilePosition: {
        [`${DialogMobilePosition.BOTTOM}`]: 'bottom-0 max-h-[calc(100%-1rem)] w-full rounded-t-xl',
        [`${DialogMobilePosition.CENTER}`]:
          'top-[50%] max-h-[calc(100%-2rem)] w-[calc(100%-2.5rem)] translate-y-[-50%] rounded-xl',
      },
    },
    defaultVariants: {
      mobilePosition: DialogMobilePosition.BOTTOM,
    },
  },
)

export interface DialogCloseButtonProps extends React.ComponentPropsWithoutRef<'button'> {}

export const DialogCloseButton = React.forwardRef<HTMLButtonElement, DialogCloseButtonProps>(
  ({ className, type = 'button', 'aria-label': ariaLabel, ...props }, ref) => {
    const { t } = getTranslate()

    return (
      <button
        ref={ref}
        className={cn(
          'flex items-center justify-center rounded-full border-none bg-transparent p-2 text-peach-500 hover:text-peach-600 active:text-peach-400',
          className,
        )}
        type={type}
        aria-label={ariaLabel ?? t('component.button.close')}
        {...props}
      >
        <CancelFullIcon aria-hidden />
      </button>
    )
  },
)
DialogCloseButton.displayName = 'DialogCloseButton'

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  hideCloseButton?: boolean
  desktopMaxWidth?: number
}

export const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, children, hideCloseButton = false, desktopMaxWidth = 540, mobilePosition, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(dialogContentVariants({ mobilePosition }), className)}
        style={{ '--maxWidth': `${desktopMaxWidth}px` } as React.CSSProperties}
        {...props}
      >
        {hideCloseButton ? null : (
          <DialogClose asChild>
            <DialogCloseButton className="-right-2 -top-2 -m-2 self-end tablet:absolute" />
          </DialogClose>
        )}
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
)
DialogContent.displayName = DialogPrimitive.Content.displayName

export interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>(({ className, ...props }, ref) => (
  // Styles TBD
  <header ref={ref} className={cn('flex flex-col items-stretch gap-2', className)} {...props} />
))
DialogHeader.displayName = 'DialogHeader'

export interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const DialogFooter = React.forwardRef<HTMLDivElement, DialogFooterProps>(({ className, ...props }, ref) => (
  // Styles TBD
  <footer
    ref={ref}
    className={cn('flex w-full flex-col justify-start gap-2 self-stretch tablet:flex-row-reverse', className)}
    {...props}
  />
))
DialogFooter.displayName = 'DialogFooter'

export interface DialogTitleProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> {}

const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, DialogTitleProps>(
  // Styles TBD
  ({ className, ...props }, ref) => (
    <Heading asChild variant="xl" className={cn('', className)}>
      <DialogPrimitive.Title ref={ref} {...props} />
    </Heading>
  ),
)
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
  // Styles TBD
>(({ className, ...props }, ref) => (
  <Text asChild className={cn('', className)}>
    <DialogPrimitive.Description ref={ref} {...props} />
  </Text>
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export { DialogHeader, DialogFooter, DialogTitle, DialogDescription }
